// contains statuses when loading data for first render on client
import { createAction, createReducer } from '@reduxjs/toolkit';
import { parseListingPageAttributesFromUrl } from '../listingPageUrlParser/listingPageUrlParser';
import { fetchProductsListingPageData, fetchUCMSHierarchy } from '../dataFetcher/dataFetcher';
import { setUcmsHierarchy } from './listingPageStaticData.reduck';
import { convertFetchedDataForListingPageToReduxFormat, storeListingPageXhrResponse } from './listingPagePages.reduck';

const log = require('loglevel');


// 1. Initial state
const initialState = {
  // it's true when data for first render of listing page on client are loading
  listingPageDataLoadingForFirstRender: false,
  // it's true when loading of data for first render of listing page on client has failed
  listingPageDataLoadingForFirstRenderFailed: false,
  // error code when loading of data for first render of listing page on client has failed
  errorCodeOnClient: undefined
};

// 2. Basic actions creators
const setListingPageDataLoadingForFirstRender =
  createAction('listingPage/commonData/setSearchListingPageFirstRenderLoading');
const setListingPageDataLoadingForFirstRenderFailed =
  createAction('listingPage/commonData/setListingPageDataLoadingForFirstRenderFailed');
const setErrorCodeOnClient =
  createAction('listingPage/commonData/setErrorCodeOnClient');

// 3. Complex actions which call basic actions above


// Loads data required for search listing on client to init the page
//
// It does following gathering and parsing actions in following order:
// 1. Parses URL of Listing Page to check if it is in valid format
// 2. Gets following data asynchronously in order to improve performance
//   - Gets UCMS Hierarchy from fo-site API
//   - Gets products data from SearchLabs API and products data from Store API
// 3. Process UCMS Hierarchy from response and store it to redux store
// 4. Process products data from response
//     - When fetchDataResult contains redirectUrl, it means that SearchLabs API requires redirecting to this URL
//     - So we redirects immediately to this URL (SIDE EFFECT!)
// 5. Converts product data from SearchLabs API and Store API to redux format and store them to redux store
//
export function loadDataForSearchListingPageForFirstRenderOnClient() {
  return async (dispatch, getState) => {
    dispatch(setListingPageDataLoadingForFirstRender(true));
    const listingPageUrl = window.location.href;

    // 1. Parses URL of Listing Page to check if it is in valid format
    const parsingAttributesResult = parseListingPageAttributesFromUrl(listingPageUrl);
    if (!parsingAttributesResult.ok) {
      log.error(parsingAttributesResult.customErrorMessage);
      dispatch(setErrorCodeOnClient(parsingAttributesResult.errorCode ? parsingAttributesResult.errorCode : 500));
      dispatch(setListingPageDataLoadingForFirstRenderFailed(true));
      dispatch(setListingPageDataLoadingForFirstRender(false));
      return;
    }
    const listingPageAttributesParsedFromURL = parsingAttributesResult.listingPageAttributes;


    // 2. Gets following data asynchronously in order to improve performance
    //   - Gets UCMS Hierarchy from fo-site API
    //   - Gets products data from SearchLabs API and products data from Store API
    const dataFetchResults = await Promise.all([
      fetchUCMSHierarchy(), // dataFetchResults[0],
      fetchProductsListingPageData(listingPageAttributesParsedFromURL) // dataFetchResults[1]
    ]);


    // 3. Process UCMS Hierarchy from response and store it to redux store
    const ucmsHierarchyResult = dataFetchResults[0];
    if (!ucmsHierarchyResult.ok) {
      // loading UCMS hierarchy from fo-site failed
      log.error('[UCMS HIERARCHY LOADING] fetch failed');
      log.error(ucmsHierarchyResult.customErrorMessage);
      dispatch(setErrorCodeOnClient(ucmsHierarchyResult.errorCode ? ucmsHierarchyResult.errorCode : 500));
      dispatch(setListingPageDataLoadingForFirstRenderFailed(true));
      dispatch(setListingPageDataLoadingForFirstRender(false));
      return;
    }
    // adding UCMS hierarchy data to redux
    dispatch(setUcmsHierarchy(ucmsHierarchyResult.ucmsHierarchy));


    // 4. Process products data from response
    //     - When fetchDataResult contains redirectUrl, it means that SearchLabs API requires redirecting to this URL
    //     - So we redirects immediately to this URL (SIDE EFFECT!)
    const fetchDataResult = dataFetchResults[1];
    if (!fetchDataResult.ok) {
      // fetching data failed
      log.error('[SEARCH LISTING PAGE DATA] fetch failed');
      log.error(fetchDataResult.customErrorMessage);
      dispatch(setErrorCodeOnClient(fetchDataResult.errorCode ? fetchDataResult.errorCode : 500));
      dispatch(setListingPageDataLoadingForFirstRenderFailed(true));
      dispatch(setListingPageDataLoadingForFirstRender(false));
      return;
    }
    // When fetchDataResult contains redirectUrl, it means that SearchLabs API requires redirecting to this URL
    // So we redirect to this URL immediately
    if (fetchDataResult.redirectUrl) {
      log.warn(`[REDIRECT] Redirecting to URL ${fetchDataResult.redirectUrl}`);
      window.location.replace(fetchDataResult.redirectUrl);

      return;
    }


    // 5. Converts product data from SearchLabs API and Store API to redux format and store them to redux store
    const conversionResult = convertFetchedDataForListingPageToReduxFormat(
      {
        ...fetchDataResult.data,
        searchLabsRequestAttrs: listingPageAttributesParsedFromURL,
        ucmsHierarchy: getState().listingPage.staticData.ucmsHierarchy
      }
    );
    if (!conversionResult.ok) {
      log.error('[SEARCH LISTING PAGE CONVERSION OF FETCHED DATA TO REDUX FORMAT] failed');
      log.error(conversionResult.customErrorMessage);
      dispatch(setErrorCodeOnClient(conversionResult.errorCode ? conversionResult.errorCode : 500));
      dispatch(setListingPageDataLoadingForFirstRenderFailed(true));
      dispatch(setListingPageDataLoadingForFirstRender(false));
      return;
    }
    // adding listing page data to redux
    dispatch(storeListingPageXhrResponse(conversionResult.convertedData));
    dispatch(setListingPageDataLoadingForFirstRender(false));
    log.info('[SEARCH LISTING PAGE DATA]  loaded');
  };
}


// 4. Reducer
/* eslint-disable no-param-reassign */
export default createReducer(initialState, {
  [setListingPageDataLoadingForFirstRender]: (stateDraft, action) => {
    stateDraft.listingPageDataLoadingForFirstRender = action.payload;
  },
  [setListingPageDataLoadingForFirstRenderFailed]: (stateDraft, action) => {
    stateDraft.listingPageDataLoadingForFirstRenderFailed = action.payload;
  },
  [setErrorCodeOnClient]: (stateDraft, action) => {
    stateDraft.errorCodeOnClient = action.payload;
  }
});
